import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AuthLayout from '../components/layout/AuthLayout.jsx';
import { useAuth } from '../contexts/AuthContext.jsx';

/** Mail dell'ultimo accesso con "Ricordami" spuntato (solo locale). */
const REMEMBER_KEY = 'minicrm.remembered_email';

function readRememberedEmail() {
  try { return localStorage.getItem(REMEMBER_KEY) || ''; } catch { return ''; }
}

function writeRememberedEmail(value) {
  try {
    if (value) localStorage.setItem(REMEMBER_KEY, value);
    else localStorage.removeItem(REMEMBER_KEY);
  } catch { /* storage non disponibile (private mode): ignora */ }
}

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState(readRememberedEmail);
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [remember, setRemember] = useState(() => Boolean(readRememberedEmail()));
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('password'); // password | pin

  async function submit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login({ email: email.trim(), password, pin: mode === 'pin' ? pin : undefined });
      writeRememberedEmail(remember ? email.trim() : '');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err?.message || 'Errore login');
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout
      title="Accedi a MiniCRM"
      subtitle="Area riservata multi-utente"
      footer={<>Non hai un account?{' '}<Link to="/register" className="text-brand-300 hover:underline">Registrati</Link></>}
    >
      <div className="text-sm text-surface-3 space-y-1">
        <p>Benvenuto! Per accedere con il tuo account inserisci le tue credenziali.</p>
        <p className="text-warning font-semibold">Ricorda di salvare la password in un gestore sicuro.</p>
      </div>

      <div className="grid grid-cols-2 gap-1 rounded-lg border border-line bg-panel-2 p-1">
        <button type="button" className={`btn w-full text-xs ${mode === 'password' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setMode('password')}>Password</button>
        <button type="button" className={`btn w-full text-xs ${mode === 'pin' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setMode('pin')}>PIN rapido</button>
      </div>

      <form onSubmit={submit} className="space-y-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-surface-4" htmlFor="login-email">Email *</label>
          <input id="login-email" className="input" type="email" autoComplete="email" autoFocus required value={email} onChange={e => setEmail(e.target.value)} />
        </div>
        {mode === 'password' ? (
          <div className="flex flex-col gap-1">
            <label className="text-xs text-surface-4" htmlFor="login-password">Password *</label>
            <input id="login-password" className="input" type="password" autoComplete="current-password" required value={password} onChange={e => setPassword(e.target.value)} />
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            <label className="text-xs text-surface-4" htmlFor="login-pin">PIN personale (4-6 cifre) *</label>
            <input id="login-pin" className="input" type="password" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{4,6}" maxLength={6} required value={pin} onChange={e => setPin(e.target.value)} placeholder="******" />
          </div>
        )}
        <div className="flex items-center gap-2 text-sm">
          <input id="remember" type="checkbox" className="accent-brand-500" checked={remember} onChange={e => setRemember(e.target.checked)} />
          <label htmlFor="remember">Ricordami</label>
          <Link className="ml-auto text-xs text-brand-300 hover:underline" to="/forgot">Password/PIN dimenticati?</Link>
        </div>
        {error && <div className="rounded-md border border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger">{error}</div>}
        <button type="submit" className="btn btn-primary w-full" disabled={loading}>{loading ? 'Accesso...' : 'Accedi'}</button>
      </form>
    </AuthLayout>
  );
}